/**
 * OverviewPage.tsx — Analytics dashboard with tabbed views.
 *
 * Tabs: Business Overview, Customers, Revenue, Subscriptions
 * Each tab shows KPI cards + charts from the consolidated dashboard stats endpoint.
 *
 * @module packages/billing-admin/src/pages/OverviewPage
 * @since 2026-06-17
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { useDashboardStats, useMrrReport, useArrReport } from '../hooks/useAdminApi50';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtMoney(cents: number): string {
  if (cents >= 100_000_00) return `$${(cents / 100_00 / 10).toFixed(1)}K`;
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtMoneyDollars(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${amount.toFixed(0)}`;
}

function fmtMonth(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

function pctChange(current: number, previous: number): string {
  if (previous === 0) return current > 0 ? '+100%' : '—';
  const pct = ((current - previous) / previous) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`;
}

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------

function KpiCard({ label, value, subtext, trend, trendType = 'neutral', icon }: {
  label: string; value: string; subtext?: string; trend?: string;
  trendType?: 'positive' | 'negative' | 'neutral'; icon?: React.ReactNode;
}): React.JSX.Element {
  const trendColor = trendType === 'positive' ? 'text-emerald-600' : trendType === 'negative' ? 'text-red-600' : 'text-slate-500';
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {icon && <div className="text-slate-400">{icon}</div>}
      </div>
      <p className="mt-3 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
      <div className="mt-2 flex items-center gap-2">
        {trend && <span className={`text-sm font-medium ${trendColor}`}>{trend}</span>}
        {subtext && <span className="text-xs text-slate-400">{subtext}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chart Card wrapper
// ---------------------------------------------------------------------------

function ChartCard({ title, children, className = '' }: {
  title: string; children: React.ReactNode; className?: string;
}): React.JSX.Element {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
      <p className="mb-4 text-sm font-semibold text-slate-700">{title}</p>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------

type Tab = 'overview' | 'customers' | 'revenue' | 'subscriptions';

const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Business Overview' },
  { key: 'customers', label: 'Customers' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'subscriptions', label: 'Subscriptions' },
];

const CHART_COLORS = ['#0891b2', '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899'];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function OverviewPage(): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const { data: stats, isLoading } = useDashboardStats();
  const { data: mrrData } = useMrrReport();
  const { data: arrData } = useArrReport();

  const monthlyCustomers = (stats?.monthly_customers ?? []).map((m) => ({ ...m, month: fmtMonth(m.month) }));
  const monthlySubs = (stats?.monthly_subscriptions ?? []).map((m) => ({ ...m, month: fmtMonth(m.month) }));
  const monthlyRevenue = (stats?.monthly_revenue ?? []).map((m) => ({
    month: fmtMonth(m.month),
    revenue: Number(m.revenue) / 100,
  }));
  const planDist = stats?.plan_distribution ?? [];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {/* Tab bar */}
        <div className="flex gap-1 rounded-xl border border-slate-200 bg-slate-100 p-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition ${
                activeTab === tab.key
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-cyan-600" />
          </div>
        )}

        {!isLoading && stats && activeTab === 'overview' && (
          <OverviewTab stats={stats} mrrData={mrrData} arrData={arrData} monthlyRevenue={monthlyRevenue} monthlyCustomers={monthlyCustomers} planDist={planDist} />
        )}
        {!isLoading && stats && activeTab === 'customers' && (
          <CustomersTab stats={stats} monthlyCustomers={monthlyCustomers} planDist={planDist} />
        )}
        {!isLoading && stats && activeTab === 'revenue' && (
          <RevenueTab stats={stats} mrrData={mrrData} arrData={arrData} monthlyRevenue={monthlyRevenue} />
        )}
        {!isLoading && stats && activeTab === 'subscriptions' && (
          <SubscriptionsTab stats={stats} monthlySubs={monthlySubs} planDist={planDist} />
        )}
      </div>
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
// OVERVIEW TAB
// ---------------------------------------------------------------------------

function OverviewTab({ stats, mrrData, arrData, monthlyRevenue, monthlyCustomers, planDist }: {
  stats: NonNullable<ReturnType<typeof useDashboardStats>['data']>;
  mrrData?: Array<{ month: string; mrr: number }>;
  arrData?: { arr: number; churnRate: number } | null;
  monthlyRevenue: Array<{ month: string; revenue: number }>;
  monthlyCustomers: Array<{ month: string; count: number }>;
  planDist: Array<{ plan_name: string; subscriber_count: number }>;
}): React.JSX.Element {
  const latestMrr = mrrData?.[mrrData.length - 1];

  return (
    <div className="space-y-6">
      {/* Top KPIs */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Total Customers"
          value={String(stats.customers.total)}
          trend={`+${stats.customers.new_30d} this month`}
          trendType={stats.customers.new_30d > 0 ? 'positive' : 'neutral'}
          subtext={`${stats.customers.new_7d} this week`}
        />
        <KpiCard
          label="Active Subscriptions"
          value={String(stats.subscriptions.active)}
          trend={`+${stats.subscriptions.new_30d} new`}
          trendType={stats.subscriptions.new_30d > 0 ? 'positive' : 'neutral'}
          subtext={`of ${stats.subscriptions.total} total`}
        />
        <KpiCard
          label="MRR"
          value={latestMrr ? fmtMoneyDollars(latestMrr.mrr) : '—'}
          subtext={arrData ? `ARR: ${fmtMoneyDollars(arrData.arr)}` : undefined}
        />
        <KpiCard
          label="Outstanding Invoices"
          value={String(stats.invoices.outstanding)}
          trend={stats.invoices.draft > 0 ? `${stats.invoices.draft} draft` : undefined}
          trendType={stats.invoices.outstanding > 0 ? 'negative' : 'neutral'}
          subtext={`${fmtMoney(Number(stats.invoices.total_outstanding))} value`}
        />
      </div>

      {/* Charts row */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Revenue Trend (12 months)">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={monthlyRevenue}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0891b2" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0891b2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v: number) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}`} />
              <Tooltip formatter={(v: number) => [`$${v.toLocaleString()}`, 'Revenue']} />
              <Area type="monotone" dataKey="revenue" stroke="#0891b2" strokeWidth={2} fill="url(#revGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Customer Growth (12 months)">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthlyCustomers}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="New Customers" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Bottom row */}
      <div className="grid gap-4 lg:grid-cols-3">
        <ChartCard title="Plan Distribution">
          {planDist.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={planDist} dataKey="subscriber_count" nameKey="plan_name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} label={({ plan_name, percent }) => `${plan_name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {planDist.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[220px] items-center justify-center text-sm text-slate-400">No subscription data yet</div>
          )}
        </ChartCard>

        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="mb-4 text-sm font-semibold text-slate-700">Quick Status</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <StatusRow label="Active Plans" value={String(stats.plans.active)} total={stats.plans.total} />
            <StatusRow label="Trialing" value={String(stats.subscriptions.trialing)} color="text-blue-600" />
            <StatusRow label="Past Due" value={String(stats.subscriptions.past_due)} color={stats.subscriptions.past_due > 0 ? 'text-amber-600' : undefined} />
            <StatusRow label="Canceled" value={String(stats.subscriptions.canceled)} color={stats.subscriptions.canceled > 0 ? 'text-red-600' : undefined} />
            <StatusRow label="Credit Accounts" value={String(stats.credits.accounts_with_credits)} />
            <StatusRow label="Enterprise Contracts" value={String(stats.contracts.total)} />
            <StatusRow label="Paid Invoices" value={String(stats.invoices.paid)} />
            <StatusRow label="Total Collected" value={fmtMoney(Number(stats.invoices.total_collected))} color="text-emerald-600" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CUSTOMERS TAB
// ---------------------------------------------------------------------------

function CustomersTab({ stats, monthlyCustomers, planDist }: {
  stats: NonNullable<ReturnType<typeof useDashboardStats>['data']>;
  monthlyCustomers: Array<{ month: string; count: number }>;
  planDist: Array<{ plan_name: string; subscriber_count: number }>;
}): React.JSX.Element {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Total Customers" value={String(stats.customers.total)} trend={`+${stats.customers.new_30d}`} trendType="positive" subtext="last 30 days" />
        <KpiCard label="New This Week" value={String(stats.customers.new_7d)} trendType={stats.customers.new_7d > 0 ? 'positive' : 'neutral'} />
        <KpiCard label="With Active Credits" value={String(stats.credits.accounts_with_credits)} subtext={`${fmtMoney(Number(stats.credits.total_balance))} balance`} />
        <KpiCard label="Enterprise Contracts" value={String(stats.contracts.total)} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Customer Growth (12 months)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyCustomers}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="New Customers" fill="#0891b2" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Customers by Plan">
          {planDist.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={planDist} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
                <YAxis dataKey="plan_name" type="category" tick={{ fontSize: 12 }} stroke="#94a3b8" width={120} />
                <Tooltip />
                <Bar dataKey="subscriber_count" name="Subscribers" fill="#6366f1" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">No plan data</div>
          )}
        </ChartCard>
      </div>

      <div className="flex justify-center">
        <Link to="/customers" className="rounded-xl border border-slate-300 px-6 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition">
          View all customers →
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// REVENUE TAB
// ---------------------------------------------------------------------------

function RevenueTab({ stats, mrrData, arrData, monthlyRevenue }: {
  stats: NonNullable<ReturnType<typeof useDashboardStats>['data']>;
  mrrData?: Array<{ month: string; mrr: number }>;
  arrData?: { arr: number; churnRate: number } | null;
  monthlyRevenue: Array<{ month: string; revenue: number }>;
}): React.JSX.Element {
  const latestMrr = mrrData?.[mrrData.length - 1];
  const prevMrr = mrrData && mrrData.length >= 2 ? mrrData[mrrData.length - 2] : null;
  const mrrChart = (mrrData ?? []).map((m) => ({ month: fmtMonth(m.month), mrr: m.mrr }));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="MRR"
          value={latestMrr ? fmtMoneyDollars(latestMrr.mrr) : '—'}
          trend={prevMrr && latestMrr ? pctChange(latestMrr.mrr, prevMrr.mrr) : undefined}
          trendType={prevMrr && latestMrr && latestMrr.mrr >= prevMrr.mrr ? 'positive' : 'negative'}
        />
        <KpiCard
          label="ARR"
          value={arrData ? fmtMoneyDollars(arrData.arr) : '—'}
        />
        <KpiCard
          label="Churn Rate"
          value={arrData ? `${(arrData.churnRate * 100).toFixed(1)}%` : '—'}
          trendType={arrData && arrData.churnRate > 0.05 ? 'negative' : 'positive'}
        />
        <KpiCard
          label="Total Collected"
          value={fmtMoney(Number(stats.invoices.total_collected))}
          subtext={`${stats.invoices.paid} invoices paid`}
          trendType="positive"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="MRR Trend">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={mrrChart}>
              <defs>
                <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v: number) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}`} />
              <Tooltip formatter={(v: number) => [`$${v.toLocaleString()}`, 'MRR']} />
              <Area type="monotone" dataKey="mrr" stroke="#10b981" strokeWidth={2} fill="url(#mrrGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Monthly Revenue (invoices paid)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyRevenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v: number) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}`} />
              <Tooltip formatter={(v: number) => [`$${v.toLocaleString()}`, 'Revenue']} />
              <Bar dataKey="revenue" name="Revenue" fill="#0891b2" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-slate-700 mb-3">Invoice Breakdown</p>
          <div className="space-y-2">
            <InvoiceRow label="Paid" count={stats.invoices.paid} color="bg-emerald-500" total={stats.invoices.total} />
            <InvoiceRow label="Outstanding" count={stats.invoices.outstanding} color="bg-amber-500" total={stats.invoices.total} />
            <InvoiceRow label="Draft" count={stats.invoices.draft} color="bg-slate-400" total={stats.invoices.total} />
          </div>
        </div>
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-semibold text-slate-700 mb-3">Revenue Summary</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <StatusRow label="Total Collected" value={fmtMoney(Number(stats.invoices.total_collected))} color="text-emerald-600" />
            <StatusRow label="Outstanding" value={fmtMoney(Number(stats.invoices.total_outstanding))} color={Number(stats.invoices.total_outstanding) > 0 ? 'text-amber-600' : undefined} />
            <StatusRow label="Credit Balances" value={fmtMoney(Number(stats.credits.total_balance))} />
            <StatusRow label="Active Plans" value={String(stats.plans.active)} total={stats.plans.total} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SUBSCRIPTIONS TAB
// ---------------------------------------------------------------------------

function SubscriptionsTab({ stats, monthlySubs, planDist }: {
  stats: NonNullable<ReturnType<typeof useDashboardStats>['data']>;
  monthlySubs: Array<{ month: string; count: number }>;
  planDist: Array<{ plan_name: string; subscriber_count: number }>;
}): React.JSX.Element {
  const totalActive = stats.subscriptions.active + stats.subscriptions.trialing;
  const statusData = [
    { name: 'Active', value: stats.subscriptions.active, color: '#10b981' },
    { name: 'Trialing', value: stats.subscriptions.trialing, color: '#6366f1' },
    { name: 'Past Due', value: stats.subscriptions.past_due, color: '#f59e0b' },
    { name: 'Canceled', value: stats.subscriptions.canceled, color: '#ef4444' },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Total Subscriptions" value={String(stats.subscriptions.total)} trend={`+${stats.subscriptions.new_30d}`} trendType="positive" subtext="new this month" />
        <KpiCard label="Active" value={String(stats.subscriptions.active)} subtext={`${totalActive} incl. trialing`} trendType="positive" />
        <KpiCard label="Past Due" value={String(stats.subscriptions.past_due)} trendType={stats.subscriptions.past_due > 0 ? 'negative' : 'neutral'} />
        <KpiCard label="Canceled" value={String(stats.subscriptions.canceled)} trendType={stats.subscriptions.canceled > 0 ? 'negative' : 'neutral'} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="New Subscriptions (12 months)">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlySubs}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name="New Subscriptions" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Status Breakdown">
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {statusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">No subscription data</div>
          )}
        </ChartCard>
      </div>

      {planDist.length > 0 && (
        <ChartCard title="Subscribers per Plan">
          <ResponsiveContainer width="100%" height={Math.max(200, planDist.length * 50)}>
            <BarChart data={planDist} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" allowDecimals={false} />
              <YAxis dataKey="plan_name" type="category" tick={{ fontSize: 12 }} stroke="#94a3b8" width={140} />
              <Tooltip />
              <Bar dataKey="subscriber_count" name="Subscribers" fill="#0891b2" radius={[0, 4, 4, 0]}>
                {planDist.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <div className="flex justify-center">
        <Link to="/subscriptions" className="rounded-xl border border-slate-300 px-6 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition">
          View all subscriptions →
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function StatusRow({ label, value, color, total }: {
  label: string; value: string; color?: string; total?: number;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
      <span className="text-sm text-slate-600">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className={`text-sm font-semibold ${color ?? 'text-slate-900'}`}>{value}</span>
        {total !== undefined && <span className="text-xs text-slate-400">/ {total}</span>}
      </div>
    </div>
  );
}

function InvoiceRow({ label, count, color, total }: {
  label: string; count: number; color: string; total: number;
}): React.JSX.Element {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="font-medium text-slate-900">{count}</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
